import { useTranslation } from 'react-i18next';
import { Button, SectionCard } from '@/components/ui';
import type { AppDetailPageVM } from './useAppDetailPage';
import { AppDetailConsiderationsCard } from './AppDetailConsiderationsCard';
import detail from './AppDetail.module.css';
import styles from './AppDetailOverviewTab.module.css';

/**
 * EAA-1817 (frame 49): Tổng quan tab.
 *
 * The mock's "Phạm vi triển khai" (per-org enable toggles) card is still
 * DELIBERATELY OMITTED: `OrganizationAppScope` has no admin-console view of
 * any kind (not even read — confirmed via `portal_api/urls.py`, only
 * `OrganizationAppScopeService` exists, unwrapped by any HTTP endpoint). Per
 * house convention (see EAA-1819's comment on the missing account-count
 * endpoint): name the gap, ship what has a real source, never fabricate a
 * card around data that does not exist.
 *
 * EAA-1884: the "Việc cần cân nhắc" (Tạm ngưng/Gỡ) card this same section used
 * to omit for the same reason IS now shipped (`AppDetailConsiderationsCard`)
 * — `suspendApp`/`resumeApp`/`retireApp` landed server-side after this comment
 * was written (see `api/console.ts`'s doc comment on `RegistryApp.lifecycle`).
 */
export function AppDetailOverviewTab({ vm }: { vm: AppDetailPageVM }) {
    const { t } = useTranslation();
    const app = vm.app;
    if (!app) return null;

    const owner =
        app.owner_name && app.owner_email
            ? `${app.owner_name} · ${app.owner_email}`
            : app.owner_name || app.owner_email || t('common.noData');

    return (
        <div className={detail.grid}>
            <div className={detail.gridMain}>
                <SectionCard title={t('console.appDetail.overview.infoCardTitle')}>
                    <dl className={detail.fieldlist}>
                        <div className={detail.fieldlistRow}>
                            <dt className={detail.fieldlistTerm}>
                                {t('console.appDetail.overview.fieldCode')}
                            </dt>
                            <dd className={detail.fieldlistValue}>
                                <code className={detail.fieldlistCode}>{app.code}</code>
                            </dd>
                        </div>
                        <div className={detail.fieldlistRow}>
                            <dt className={detail.fieldlistTerm}>
                                {t('console.appDetail.overview.fieldName')}
                            </dt>
                            <dd className={detail.fieldlistValue}>{app.name}</dd>
                        </div>
                        <div className={detail.fieldlistRow}>
                            <dt className={detail.fieldlistTerm}>
                                {t('console.appDetail.overview.fieldWebUrl')}
                            </dt>
                            <dd className={`${detail.fieldlistValue} ${detail.isLink}`}>
                                {app.web_url}
                            </dd>
                        </div>
                        <div className={detail.fieldlistRow}>
                            <dt className={detail.fieldlistTerm}>
                                {t('console.appDetail.overview.fieldAudience')}
                            </dt>
                            <dd className={detail.fieldlistValue}>
                                <code className={detail.fieldlistCode}>{app.audience}</code>
                            </dd>
                        </div>
                        <div className={detail.fieldlistRow}>
                            <dt className={detail.fieldlistTerm}>
                                {t('console.appDetail.overview.fieldOwner')}
                            </dt>
                            <dd className={detail.fieldlistValue}>{owner}</dd>
                        </div>
                    </dl>
                </SectionCard>
            </div>

            <div className={detail.gridSide}>
                <SectionCard title={t('console.appDetail.overview.statusCardTitle')}>
                    <div
                        className={`${styles.statusBanner} ${
                            app.enabled ? detail.isApproved : detail.isPending
                        }`}
                    >
                        {app.enabled
                            ? t('console.appDetail.overview.statusApprovedBanner')
                            : t('console.appDetail.overview.statusPendingBanner')}
                    </div>
                    <div className={styles.metricList}>
                        <div className={styles.metricListRow}>
                            <span className={styles.metricLabel}>
                                {t('console.appDetail.overview.metricRoleCatalog')}
                            </span>
                            <strong>{app.catalog_role_count}</strong>
                        </div>
                        <div className={styles.metricListRow}>
                            <span className={styles.metricLabel}>
                                {t('console.appDetail.overview.metricUnconverted')}
                            </span>
                            <strong className={detail.isWarning}>{vm.unconvertedCount}</strong>
                        </div>
                    </div>
                    <p className={detail.hint}>
                        {app.catalog_last_synced_at
                            ? t('console.appDetail.overview.lastSynced', {
                                  time: new Date(app.catalog_last_synced_at).toLocaleString('vi-VN'),
                              })
                            : t('console.appDetail.overview.neverSynced')}
                    </p>
                </SectionCard>

                <SectionCard title={t('console.appDetail.overview.recentActivityTitle')}>
                    {!vm.canViewAudit && (
                        <p className="portal-admin-hint">{t('console.appDetail.audit.accessDenied')}</p>
                    )}
                    {vm.canViewAudit && vm.auditQuery.isLoading && (
                        <p className="portal-admin-hint">{t('common.loading')}</p>
                    )}
                    {vm.canViewAudit && vm.auditQuery.isSuccess && vm.auditRows.length === 0 && (
                        <p className="portal-admin-hint">
                            {t('console.appDetail.overview.recentActivityEmpty')}
                        </p>
                    )}
                    {vm.canViewAudit && vm.auditRows.length > 0 && (
                        <ul className={detail.activityList}>
                            {vm.auditRows.slice(0, 4).map((row) => (
                                <li key={row.id} className={detail.activityItem}>
                                    <span className={detail.activityListTime}>
                                        {new Date(row.created_at).toLocaleString('vi-VN')}
                                    </span>
                                    <span className={detail.activityListBody}>
                                        <strong className={detail.activityBodyTitle}>
                                            {t(`console.appDetail.audit.action.${row.action}`, {
                                                defaultValue: row.action,
                                            })}
                                        </strong>
                                        <span className={detail.activityBodyText}>
                                            {row.actor_label ||
                                                t('console.appDetail.audit.systemActor')}
                                        </span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {vm.canViewAudit && (
                        <Button
                            type="link"
                            size="small"
                            className={detail.linkButton}
                            onClick={() => vm.setTab('audit')}
                        >
                            {t('console.appDetail.overview.viewFullAudit')}
                        </Button>
                    )}
                </SectionCard>

                <AppDetailConsiderationsCard app={app} canManage={vm.canManage} />
            </div>
        </div>
    );
}

export default AppDetailOverviewTab;
